import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { InfoTooltip } from '../components/InfoTooltip';
import { api } from '../services/api';
import { InventoryItem, InventoryGroup, ProductLine, ProductLineVersion, StockTakeType, ComponentType, APPLIES_TO_OPTIONS, BomAssemblyItem, ASSEMBLY_TYPES, AssemblyType, POSITION_DEDUCTION_METHODS } from '../types';
import clsx from 'clsx';

// Stock take type options for dropdown
const STOCK_TAKE_TYPES: { value: StockTakeType; label: string; description: string }[] = [
  { value: 'fixed', label: 'Fixed', description: 'Always exact quantity (e.g., "2" end plates)' },
  { value: 'per_screen', label: 'Per Screen', description: 'Fixed qty per screen (e.g., "4" wheels)' },
  { value: 'per_order', label: 'Per Order', description: 'Once per order, not per screen' },
  { value: 'color_variant', label: 'Color Variant', description: 'Match system color (Black, Bronze, White, Tan, Mill Finish)' },
  { value: 'calculated', label: 'Calculated', description: 'Formula based (e.g., "holes × 2" springs)' },
  { value: 'cut_length', label: 'Cut Length', description: 'Linear cut from stock (e.g., roller tube)' },
  { value: 'linear_from_roll', label: 'Linear From Roll', description: 'Consumption from roll (e.g., fabric)' },
  { value: 'conditional', label: 'Conditional', description: 'Only if option selected (e.g., U-channel)' },
];

// Component type options for dropdown
// Backend accepts any string for component_type — these are just suggestions.
// Includes legacy values that exist in the DB (Demo, Sample, Brush Seal, etc.)
// so existing items don't lose their categorization on edit.
const COMPONENT_TYPES: { value: string; label: string }[] = [
  { value: 'Track', label: 'Track' },
  { value: 'Housing', label: 'Housing' },
  { value: 'Housing Cover', label: 'Housing Cover' },
  { value: 'Roller', label: 'Roller' },
  { value: 'WeightBar', label: 'Weight Bar' },
  { value: 'Fabric', label: 'Fabric' },
  { value: 'UChannel', label: 'U-Channel' },
  { value: 'ScrewCover', label: 'Screw Cover' },
  { value: 'Hardware', label: 'Hardware' },
  { value: 'Packaging', label: 'Packaging' },
  { value: 'IMP', label: 'IMP (Injection Molded)' },
  { value: 'Assembly', label: 'Assembly' },
  { value: 'Screw', label: 'Screw' },
  { value: 'Nut', label: 'Nut' },
  { value: 'Rivet', label: 'Rivet' },
  { value: 'Plate', label: 'Plate' },
  { value: 'Bracket', label: 'Bracket' },
  { value: 'Clip', label: 'Clip' },
  { value: 'Motor', label: 'Motor' },
  { value: 'Material', label: 'Material' },
  { value: 'Remote', label: 'Remote' },
  // Accessories / controls
  { value: 'Wall Switch', label: 'Wall Switch' },
  { value: 'Sensor', label: 'Sensor' },
  { value: 'Brush Seal', label: 'Brush Seal' },
  { value: 'Crown', label: 'Crown' },
  { value: 'Adapter', label: 'Adapter' },
  { value: 'Frame', label: 'Frame' },
  { value: 'Stand', label: 'Stand' },
  // Non-production / marketing
  { value: 'Demo', label: 'Demo' },
  { value: 'Demo Accessory', label: 'Demo Accessory' },
  { value: 'Sample', label: 'Sample' },
  { value: 'Sample Kit', label: 'Sample Kit' },
  { value: 'Starter Kit', label: 'Starter Kit' },
  { value: 'Trade Show', label: 'Trade Show' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Technical', label: 'Technical' },
];

// Applies to labels for checkbox grid
const APPLIES_TO_LABELS: Record<string, string> = {
  all: 'All screens',
  // Side-specific
  left_side: 'Left Side',
  right_side: 'Right Side',
  // U-Channel options
  u_channel_left: 'U-Channel Left',
  u_channel_right: 'U-Channel Right',
  u_channel_both: 'U-Channel Both',
  // Screw cover
  screw_cover_left: 'Screw Cover Left',
  screw_cover_right: 'Screw Cover Right',
  // Brush sizes
  brush_1_inch: '1" Brush',
  brush_2_inch: '2" Brush',
  // Fabric types
  fabric_solar: 'Solar Fabric',
  fabric_solar_premium: 'Premium Solar',
  fabric_vinyl: 'Vinyl Fabric',
  fabric_insect: 'Insect Screen',
  fabric_window: 'Window/Glass',
  // Motor types
  motor_alpha: 'Alpha Motor',
  motor_somfy: 'Somfy Motor',
  // Frame colors
  color_black: 'Black',
  color_white: 'White',
  color_bronze: 'Bronze',
  color_tan: 'Tan',
  color_mill_finish: 'Mill Finish',
  color_custom: 'Custom Powder Coat',
  // Motor types
  motor_keder: 'Keder Motor',
  // Other options
  seam_required: 'Seam Required',
  biased_cut: 'Biased Cut',
  // Shipping methods
  shipping_will_call: 'Will Call',
  shipping_freight: 'Freight Shipping',
};

// Types for this page
type SortField = 'part_number' | 'description' | 'supplier_name' | 'inventory_group' | 'on_hand' | 'reorder_quantity';
type SortDirection = 'asc' | 'desc';
type StockFilter = 'all' | 'low' | 'out' | 'ok';
type AdjustmentType = 'add' | 'remove' | 'set';

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

// Toast notification component
function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-3 animate-slide-up ${
            toast.type === 'success' ? 'bg-green-600' :
            toast.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
          }`}
        >
          <span>{toast.message}</span>
          <button onClick={() => onDismiss(toast.id)} className="text-white/80 hover:text-white">×</button>
        </div>
      ))}
    </div>
  );
}

// Stock status badge
function StockBadge({ onHand, reorderQty }: { onHand: number; reorderQty: number }) {
  if (onHand === 0) {
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Out</span>;
  }
  if (onHand <= reorderQty) {
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Low</span>;
  }
  return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">OK</span>;
}

// Sort indicator
function SortIndicator({ field, currentField, direction }: { field: SortField; currentField: SortField; direction: SortDirection }) {
  if (field !== currentField) return <span className="text-gray-300 ml-1">↕</span>;
  return <span className="text-blue-600 ml-1">{direction === 'asc' ? '↑' : '↓'}</span>;
}

// Calculate spring holes based on height
function calculateSpringHoles(H: number): number {
  if (H < 36) return 2;
  const A = H - 20; // Available span
  const k_min = Math.ceil(A / 20);
  const k_max = Math.floor(A / 16);
  if (k_max < k_min) return k_min + 1;
  const k = H > 144 ? k_max : k_min;
  return k + 1;
}

// Formula calculator for preview
function evaluateFormula(formula: string, W_system: number, H_system: number): number | null {
  if (!formula || formula.trim() === '') return null;

  const holes = calculateSpringHoles(H_system);
  const factory_ded = 0; // Default to 0 for preview (only applies when order has "Apply Factory Deductions" enabled)
  const seam_count = (H_system + 10) > 120 ? 1 : 0;

  // boxW = box width for housing/roller (same as W_system in preview — no order-specific deductions)
  const boxW = W_system;

  // Replace variables with values
  let expr = formula
    .replace(/boxW/g, String(boxW))
    .replace(/W_system/gi, String(W_system))
    .replace(/H_system/gi, String(H_system))
    .replace(/holes/gi, String(holes))
    .replace(/u_channel_ded/gi, '0')
    .replace(/factory_ded/gi, String(factory_ded))
    .replace(/seam_count/gi, String(seam_count))
    .replace(/×/g, '*')
    .replace(/÷/g, '/');

  try {
    // Safe evaluation - only allow numbers and basic operators
    if (!/^[\d\s+\-*/().]+$/.test(expr)) {
      return null;
    }
    // eslint-disable-next-line no-eval
    const result = eval(expr);
    return typeof result === 'number' && !isNaN(result) ? result : null;
  } catch {
    return null;
  }
}

// Format quantity with display unit conversion (e.g., "266.7 yds" for items tracked in yards)
// Database always stores in base unit (inches), this converts for display
function formatQuantityWithUnit(quantity: number, item: InventoryItem): string {
  const displayUnit = (item as any).displayUnit || (item as any).display_unit;
  const conversionFactor = (item as any).conversionFactor || (item as any).conversion_factor || 1;

  if (!displayUnit || displayUnit === 'each' || conversionFactor === 1) {
    return quantity.toString();
  }

  const displayValue = (quantity / conversionFactor).toFixed(1);
  return `${displayValue} ${displayUnit}`;
}

// Get raw quantity value (handles camelCase and snake_case)
function getQuantityOnHand(item: InventoryItem): number {
  return (item as any).quantityOnHand ?? item.on_hand ?? 0;
}

// Item Modal for Add/Edit with BOM Rules
function ItemModal({
  item,
  groups,
  productLines,
  onSave,
  onClose
}: {
  item: InventoryItem | null;
  groups: InventoryGroup[];
  productLines: ProductLine[];
  onSave: (data: Partial<InventoryItem>) => Promise<void>;
  onClose: () => void;
}) {
  // Helper to get value from either camelCase or snake_case field
  const i = item as any;
  // Initialize inventory_groups from array or legacy single value
  const initGroups = i?.inventoryGroups || i?.inventory_groups ||
    (i?.inventoryGroup || i?.inventory_group ? [i?.inventoryGroup || i?.inventory_group] : []);
  const initProductLines = i?.productLines || i?.product_lines || [];
  const initProductLineVersions = i?.productLineVersions || i?.product_line_versions || [];

  const [formData, setFormData] = useState({
    part_number: i?.partNumber || i?.part_number || '',
    supplier_part_number: i?.supplierPartNumber || i?.supplier_part_number || '',
    sku: i?.sku || '',
    description: i?.description || '',
    supplier_description: i?.supplierDescription || i?.supplier_description || '',
    inventory_group: i?.inventoryGroup || i?.inventory_group || '',
    inventory_groups: initGroups as string[],
    product_lines: initProductLines as string[],
    product_line_versions: initProductLineVersions as string[],
    supplier_name: i?.supplierName || i?.supplier_name || '',
    unit_price: (i?.unitCost ?? i?.unit_price ?? 0).toString(),
    dealer_price: (i?.dealerPrice ?? i?.dealer_price ?? '').toString(),
    on_hand: (i?.quantityOnHand ?? i?.on_hand ?? 0).toString(),
    reorder_quantity: (i?.reorderQuantity ?? i?.reorder_quantity ?? 10).toString(),
    min_quantity: (i?.minQuantity ?? i?.min_quantity ?? 0).toString(),
    max_quantity: (i?.maxQuantity ?? i?.max_quantity ?? 0).toString(),
    supplier_min_order: (i?.supplierMinOrder ?? i?.supplier_min_order ?? 0).toString(),
    lead_time_days: (i?.leadTimeDays ?? i?.lead_time_days ?? 7).toString(),
    inventory_type: i?.inventory_type || 'STOCKED',
    active: (i?.isActive ?? i?.active) !== false,
    // BOM Rules Fields
    stock_take_type: i?.stockTakeType || i?.stock_take_type || '',
    quantity_formula: i?.quantityFormula || i?.quantity_formula || '',
    cut_formula: i?.cutFormula || i?.cut_formula || '',
    component_type: i?.componentType || i?.component_type || '',
    applies_to: i?.appliesTo || i?.applies_to || [],
    is_extrusion: i?.isExtrusion || i?.is_extrusion || false,
    stock_length: (i?.stockLength ?? i?.stock_length ?? 288).toString(),
    min_usable_drop: (i?.minUsableDrop ?? i?.min_usable_drop ?? 84).toString(),
    roll_width: (i?.rollWidth ?? i?.roll_width ?? '').toString(),
  });
  const [saving, setSaving] = useState(false);
  const [showBomSection, setShowBomSection] = useState(
    !!(i?.stockTakeType || i?.stock_take_type || i?.quantityFormula || i?.quantity_formula || i?.cutFormula || i?.cut_formula)
  );

  // Assembly Rules state
  const [assemblyRules, setAssemblyRules] = useState<BomAssemblyItem[]>([]);
  const [showAssemblySection, setShowAssemblySection] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [newRule, setNewRule] = useState<Partial<BomAssemblyItem> | null>(null);

  // Load assembly rules when editing an existing item
  useEffect(() => {
    if (item?.id) {
      setLoadingRules(true);
      api.inventory.getAssemblyItems(item.id)
        .then(rules => {
          setAssemblyRules(rules);
          if (rules.length > 0) setShowAssemblySection(true);
        })
        .catch(console.error)
        .finally(() => setLoadingRules(false));
    }
  }, [item?.id]);

  const handleAddRule = async () => {
    if (!item?.id || !newRule?.assemblyType || !newRule?.quantityFormula) return;
    try {
      const created = await api.inventory.createAssemblyItem({
        inventoryItemId: item.id,
        assemblyType: newRule.assemblyType,
        quantityFormula: newRule.quantityFormula,
        cutFormula: newRule.cutFormula,
        appliesTo: newRule.appliesTo || [],
        workstation: newRule.workstation,
        notes: newRule.notes,
        // Positioning fields
        motorSidePosition: newRule.motorSidePosition,
        idlerSidePosition: newRule.idlerSidePosition,
        positionDeductionApplies: newRule.positionDeductionApplies,
        positionDeductionMethod: newRule.positionDeductionMethod,
      });
      setAssemblyRules([...assemblyRules, created]);
      setNewRule(null);
    } catch (err) {
      console.error('Failed to add rule:', err);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await api.inventory.deleteAssemblyItem(ruleId);
      setAssemblyRules(assemblyRules.filter(r => r.id !== ruleId));
    } catch (err) {
      console.error('Failed to delete rule:', err);
    }
  };

  // Preview calculations for 120" x 100" sample
  const previewCalc = useMemo(() => {
    const W = 120; // Sample width
    const H = 100; // Sample height
    const holes = calculateSpringHoles(H);

    let quantity: number | null = null;
    let cutSize: number | null = null;

    if (formData.quantity_formula) {
      quantity = evaluateFormula(formData.quantity_formula, W, H);
    }
    if (formData.cut_formula) {
      cutSize = evaluateFormula(formData.cut_formula, W, H);
    }

    return { W, H, holes, quantity, cutSize };
  }, [formData.quantity_formula, formData.cut_formula]);

  const handleAppliesToChange = (condition: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      applies_to: checked
        ? [...prev.applies_to, condition]
        : prev.applies_to.filter(c => c !== condition)
    }));
  };

  const handleInventoryGroupChange = (groupName: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      inventory_groups: checked
        ? [...prev.inventory_groups, groupName]
        : prev.inventory_groups.filter(g => g !== groupName),
      // Keep legacy field synced to first group
      inventory_group: checked
        ? (prev.inventory_groups.length === 0 ? groupName : prev.inventory_group)
        : (prev.inventory_groups.filter(g => g !== groupName)[0] || '')
    }));
  };

  const handleProductLineChange = (code: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      product_lines: checked
        ? [...prev.product_lines, code]
        : prev.product_lines.filter(p => p !== code)
    }));
  };

  const handleVersionChange = (versionId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      product_line_versions: checked
        ? [...prev.product_line_versions, versionId]
        : prev.product_line_versions.filter(v => v !== versionId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...formData,
        unit_price: parseFloat(formData.unit_price) || 0,
        dealer_price: formData.dealer_price === '' || formData.dealer_price == null ? undefined : (parseFloat(formData.dealer_price) || 0),
        on_hand: parseFloat(formData.on_hand) || 0,
        reorder_quantity: parseInt(formData.reorder_quantity) || 10,
        min_quantity: parseFloat(formData.min_quantity) || 0,
        max_quantity: parseFloat(formData.max_quantity) || 0,
        supplier_min_order: parseFloat(formData.supplier_min_order) || 0,
        lead_time_days: parseInt(formData.lead_time_days) || 7,
        // Multi-group and product lines (camelCase for API)
        inventoryGroups: formData.inventory_groups.length > 0 ? formData.inventory_groups : undefined,
        productLines: formData.product_lines.length > 0 ? formData.product_lines : undefined,
        productLineVersions: formData.product_line_versions.length > 0 ? formData.product_line_versions : undefined,
        // BOM fields - only include if section is shown (use camelCase for API)
        stockTakeType: showBomSection && formData.stock_take_type ? formData.stock_take_type as StockTakeType : undefined,
        quantityFormula: showBomSection ? formData.quantity_formula || undefined : undefined,
        cutFormula: showBomSection ? formData.cut_formula || undefined : undefined,
        componentType: showBomSection && formData.component_type ? formData.component_type as ComponentType : undefined,
        appliesTo: showBomSection && formData.applies_to.length > 0 ? formData.applies_to : undefined,
        isExtrusion: showBomSection ? formData.is_extrusion : undefined,
        // Stock length and min usable drop apply to extrusions AND fabric/linear items
        stockLength: showBomSection && (formData.is_extrusion || formData.component_type === 'Fabric' || formData.stock_take_type === 'linear_from_roll') ? parseFloat(formData.stock_length) || undefined : undefined,
        minUsableDrop: showBomSection && (formData.is_extrusion || formData.component_type === 'Fabric' || formData.stock_take_type === 'linear_from_roll') ? parseFloat(formData.min_usable_drop) || undefined : undefined,
        rollWidth: showBomSection && formData.roll_width ? parseFloat(formData.roll_width) || undefined : undefined,
      });
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{item ? 'Edit Item' : 'Add New Item'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Basic Info</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Part Number *</label>
                <input
                  type="text"
                  required
                  value={formData.part_number}
                  onChange={e => setFormData({ ...formData, part_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Part No</label>
                <input
                  type="text"
                  value={formData.supplier_part_number}
                  onChange={e => setFormData({ ...formData, supplier_part_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                required
                rows={2}
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Display description (what makes sense to you)"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Description</label>
              <textarea
                rows={2}
                value={formData.supplier_description}
                onChange={e => setFormData({ ...formData, supplier_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Vendor's description (for reordering)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={e => setFormData({ ...formData, sku: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Catalog SKU"
                />
              </div>
            </div>

            {/* Inventory Groups - Multi-select checkboxes */}
            <div className="mt-4 border border-gray-200 rounded-lg p-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Groups *</label>
              <p className="text-xs text-gray-500 mb-2">Select all groups this part belongs to</p>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {groups.filter(g => g.isActive).map(g => (
                  <label key={g.id} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={formData.inventory_groups.includes(g.name)}
                      onChange={e => handleInventoryGroupChange(g.name, e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{g.name}</span>
                  </label>
                ))}
              </div>
              {formData.inventory_groups.length === 0 && (
                <p className="text-xs text-red-500 mt-2">Select at least one group</p>
              )}
            </div>

            {/* Product Lines & Versions - Multi-select checkboxes */}
            {productLines && productLines.length > 0 && (
              <div className="mt-4 border border-gray-200 rounded-lg p-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Lines & Versions</label>
                <p className="text-xs text-gray-500 mb-2">Select which product line versions this part applies to. Select versions for specific BOM locking, or just the product line for all versions.</p>
                <div className="space-y-3">
                  {productLines.filter(pl => pl.isActive).map(pl => {
                    const activeVersions = (pl.versions || []).filter(v => v.status === 'active' || v.status === 'warranty_only');
                    const hasVersions = activeVersions.length > 0;

                    return (
                      <div key={pl.id} className="border border-gray-100 rounded p-2 bg-gray-50">
                        {/* Product Line checkbox */}
                        <label className="flex items-center gap-2 cursor-pointer text-sm hover:bg-gray-100 p-1 rounded font-medium">
                          <input
                            type="checkbox"
                            checked={formData.product_lines.includes(pl.code || '')}
                            onChange={e => handleProductLineChange(pl.code || '', e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-gray-800">{pl.name}</span>
                          <code className="text-xs text-gray-500 bg-gray-200 px-1 rounded">{pl.code}</code>
                        </label>

                        {/* Versions under each product line */}
                        {hasVersions && (
                          <div className="ml-6 mt-2 flex flex-wrap gap-2">
                            {activeVersions.map(version => {
                              const versionLabel = `${pl.name} V${version.versionNumber}`;
                              const statusColor = version.status === 'active'
                                ? 'text-green-600'
                                : 'text-amber-600';

                              return (
                                <label
                                  key={version.id}
                                  className="flex items-center gap-1.5 cursor-pointer text-sm hover:bg-white p-1.5 rounded border border-gray-200 bg-white"
                                  title={version.versionName || `Version ${version.versionNumber}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.product_line_versions.includes(version.id)}
                                    onChange={e => handleVersionChange(version.id, e.target.checked)}
                                    className="w-3.5 h-3.5 text-purple-600 rounded focus:ring-purple-500"
                                  />
                                  <span className="text-gray-700">V{version.versionNumber}</span>
                                  <span className={clsx('text-xs', statusColor)}>
                                    ({version.status === 'active' ? 'Active' : 'Warranty'})
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Tip: Check product line for all versions, or specific versions for version-locked BOMs.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                <input
                  type="text"
                  value={formData.supplier_name}
                  onChange={e => setFormData({ ...formData, supplier_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Stock Info Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Stock Info</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.unit_price}
                  onChange={e => setFormData({ ...formData, unit_price: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Not set"
                  value={formData.dealer_price}
                  onChange={e => setFormData({ ...formData, dealer_price: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-[11px] text-gray-400 mt-0.5">What dealers pay on the parts page. Blank = "price on request".</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">On Hand</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.on_hand}
                  onChange={e => setFormData({ ...formData, on_hand: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Qty</label>
                <input
                  type="number"
                  min="0"
                  value={formData.reorder_quantity}
                  onChange={e => setFormData({ ...formData, reorder_quantity: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Qty</label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_quantity}
                  onChange={e => setFormData({ ...formData, min_quantity: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Min order from supplier"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Qty</label>
                <input
                  type="number"
                  min="0"
                  value={formData.max_quantity}
                  onChange={e => setFormData({ ...formData, max_quantity: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Max inventory to hold"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Min Order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.supplier_min_order}
                  onChange={e => setFormData({ ...formData, supplier_min_order: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 600 yards"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Time (days)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.lead_time_days}
                  onChange={e => setFormData({ ...formData, lead_time_days: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inventory Type</label>
                <select
                  value={formData.inventory_type}
                  onChange={e => setFormData({ ...formData, inventory_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="STOCKED">Stocked</option>
                  <option value="SPECIAL_ORDER">Special Order</option>
                </select>
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={e => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
          </div>

          {/* BOM Rules Section - Collapsible */}
          <div className="border border-blue-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowBomSection(!showBomSection)}
              className="w-full px-4 py-3 bg-blue-50 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
            >
              <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-2">
                <span>BOM / Stock Take Rules</span>
                {(formData.stock_take_type || formData.quantity_formula) && (
                  <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded-full">Configured</span>
                )}
              </span>
              <span className="text-blue-600">{showBomSection ? '▼' : '▶'}</span>
            </button>

            {showBomSection && (
              <div className="p-4 space-y-4 bg-white">
                {/* Stock Take Type & Component Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stock Take Type <InfoTooltip text="length = measured in inches/feet, quantity = counted as whole units, area = square footage." /></label>
                    <select
                      value={formData.stock_take_type}
                      onChange={e => setFormData({ ...formData, stock_take_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type...</option>
                      {STOCK_TAKE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    {formData.stock_take_type && (
                      <p className="mt-1 text-xs text-gray-500">
                        {STOCK_TAKE_TYPES.find(t => t.value === formData.stock_take_type)?.description}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Component Type <InfoTooltip text="Category for workstation filtering: extrusion, fabric, hardware, packaging, electrical." /></label>
                    <select
                      value={formData.component_type}
                      onChange={e => setFormData({ ...formData, component_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select component...</option>
                      {COMPONENT_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Workstation assignment</p>
                  </div>
                </div>

                {/* Quantity Formula */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Formula <InfoTooltip text="How many of this item needed per screen. Use 1, 2, or formulas like 'u_channel_sides'." /></label>
                  <input
                    type="text"
                    value={formData.quantity_formula}
                    onChange={e => setFormData({ ...formData, quantity_formula: e.target.value })}
                    placeholder="e.g., holes × 2, 4, H_system × 2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Variables: <code className="bg-gray-100 px-1 rounded">W_system</code>,
                    <code className="bg-gray-100 px-1 rounded ml-1">H_system</code>,
                    <code className="bg-gray-100 px-1 rounded ml-1">holes</code>,
                    <code className="bg-gray-100 px-1 rounded ml-1">seam_count</code>
                  </p>
                </div>

                {/* Cut Formula */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cut Formula <InfoTooltip text="Calculation for cut length. Variables: W_system (system width, includes u-channel ded), H_system (height), boxW (box width for housing/roller), u_channel_ded (raw u-channel deduction)." /></label>
                  <input
                    type="text"
                    value={formData.cut_formula}
                    onChange={e => setFormData({ ...formData, cut_formula: e.target.value })}
                    placeholder="e.g., W_system - 14.5, H_system"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Example: <code className="bg-gray-100 px-1 rounded">W_system - 14.5</code> for roller tube
                  </p>
                </div>

                {/* Applies To Checkbox Grid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Applies To <InfoTooltip text="Which product types use this component: solar, premium_solar, cvw (vinyl), insect, or all." /></label>
                  <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg">
                    {APPLIES_TO_OPTIONS.map(condition => (
                      <label key={condition} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={formData.applies_to.includes(condition)}
                          onChange={e => handleAppliesToChange(condition, e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-gray-700">{APPLIES_TO_LABELS[condition]}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Extrusion Tracking */}
                <div className="border border-gray-200 rounded-lg p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_extrusion}
                      onChange={e => setFormData({ ...formData, is_extrusion: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">This is an extrusion (track drops/waste) <InfoTooltip text="Toggle ON for items cut from long stock (tracks, tubes, housing). Enables drop tracking." /></span>
                  </label>
                </div>

                {/* Stock Length & Min Usable Drop - for Extrusions OR Fabric/Linear items */}
                {(formData.is_extrusion || formData.component_type === 'Fabric' || formData.stock_take_type === 'linear_from_roll') && (
                  <div className="border border-gray-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      {formData.is_extrusion ? 'Extrusion Stock' : 'Roll Stock'} Dimensions
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Stock Length (inches) <InfoTooltip text="Total length of stock. For extrusions: 288&quot; (24') or 324&quot; (27'). For fabric rolls: total roll length in inches." />
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={formData.stock_length}
                          onChange={e => setFormData({ ...formData, stock_length: e.target.value })}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder={formData.is_extrusion ? "288 or 324" : "e.g., 1800 (50 yards)"}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          {formData.is_extrusion ? '288" (24\') or 324" (27\')' : 'Total roll length (e.g., 1800" = 50 yards)'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Usable Drop (inches) <InfoTooltip text="Minimum usable piece length. Smaller pieces are waste." />
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={formData.min_usable_drop}
                          onChange={e => setFormData({ ...formData, min_usable_drop: e.target.value })}
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder="84"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">Default: 84" (7')</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Roll Width - for Fabric or Linear From Roll items */}
                {(formData.component_type === 'Fabric' || formData.stock_take_type === 'linear_from_roll') && (
                  <div className="border border-orange-200 rounded-lg p-3 bg-orange-50">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Roll Width (inches) <InfoTooltip text="The HEIGHT of the fabric roll (cross-roll dimension). Used for seam detection: if fabric cut height (H + 10) > roll width, a seam is required." />
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.roll_width}
                      onChange={e => setFormData({ ...formData, roll_width: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="e.g., 120 for 10' roll"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Common sizes: 96" (8'), 120" (10'), 144" (12'). Seam required if fabric cut height (H + 10) exceeds this.
                    </p>
                  </div>
                )}

                {/* Preview Calculator */}
                {(formData.quantity_formula || formData.cut_formula) && (
                  <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <h4 className="text-sm font-semibold text-green-800 mb-3">
                      Preview (Sample: {previewCalc.W}" × {previewCalc.H}")
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-green-600">Holes calculated:</span>
                        <span className="ml-2 font-mono font-medium text-green-800">{previewCalc.holes}</span>
                      </div>
                      {formData.quantity_formula && (
                        <div>
                          <span className="text-green-600">Quantity:</span>
                          <span className="ml-2 font-mono font-medium text-green-800">
                            {previewCalc.quantity !== null ? previewCalc.quantity.toFixed(2) : 'Invalid formula'}
                          </span>
                        </div>
                      )}
                      {formData.cut_formula && (
                        <div>
                          <span className="text-green-600">Cut Size:</span>
                          <span className="ml-2 font-mono font-medium text-green-800">
                            {previewCalc.cutSize !== null ? `${previewCalc.cutSize.toFixed(2)}"` : 'Invalid formula'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assembly Rules Section */}
          <div className="border border-purple-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowAssemblySection(!showAssemblySection)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-purple-50 rounded-t-lg"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-purple-800 uppercase tracking-wide">
                  Assembly Rules
                </span>
                {assemblyRules.length > 0 && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                    {assemblyRules.length}
                  </span>
                )}
                {loadingRules && <span className="text-xs text-gray-500">(loading...)</span>}
                {!item?.id && <span className="text-xs text-gray-400">(save item first)</span>}
              </div>
              <span className="text-purple-600">{showAssemblySection ? '▼' : '▶'}</span>
            </button>

            {showAssemblySection && !item?.id && (
              <div className="p-4 border-t border-purple-200 bg-purple-50/50">
                <p className="text-sm text-purple-700">
                  Save the item first, then you can add assembly rules to define how this part is used in different assemblies (housing, track, weight bar, etc.)
                </p>
              </div>
            )}

            {showAssemblySection && item?.id && (
                <div className="p-4 border-t border-purple-200 bg-purple-50/50 space-y-4">
                  <p className="text-xs text-purple-700">
                    Define how this part is used in different assemblies. Same part can have different quantities in housing vs track vs weight bar.
                  </p>

                  {/* Existing Rules */}
                  {assemblyRules.length > 0 && (
                    <div className="space-y-2">
                      {assemblyRules.map(rule => (
                        <div key={rule.id} className="p-3 bg-white rounded-lg border border-purple-100">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                              <div>
                                <span className="text-purple-600 text-xs uppercase">Assembly</span>
                                <div className="font-medium capitalize">{rule.assemblyType.replace('_', ' ')}</div>
                              </div>
                              <div>
                                <span className="text-purple-600 text-xs uppercase">Quantity</span>
                                <div className="font-mono">{rule.quantityFormula}</div>
                              </div>
                              <div>
                                <span className="text-purple-600 text-xs uppercase">Cut Formula</span>
                                <div className="font-mono text-gray-600">{rule.cutFormula || '-'}</div>
                              </div>
                              <div>
                                <span className="text-purple-600 text-xs uppercase">Workstation</span>
                                <div className="text-gray-600">{rule.workstation || '-'}</div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDeleteRule(rule.id)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Delete rule"
                            >
                              🗑️
                            </button>
                          </div>
                          {/* Positioning info if set */}
                          {(rule.motorSidePosition != null || rule.idlerSidePosition != null) && (
                            <div className="mt-2 pt-2 border-t border-purple-100">
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-purple-600 text-xs uppercase">Positioning:</span>
                                {rule.motorSidePosition != null && (
                                  <span className="font-mono">Motor: {rule.motorSidePosition}"</span>
                                )}
                                {rule.idlerSidePosition != null && (
                                  <span className="font-mono">Idler: {rule.idlerSidePosition}"</span>
                                )}
                                {rule.positionDeductionApplies && (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                    +Deductions ({rule.positionDeductionMethod?.replace('_', ' ')})
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Rule Form */}
                  {newRule ? (
                    <div className="p-4 bg-white rounded-lg border-2 border-purple-300 space-y-3">
                      <h4 className="text-sm font-semibold text-purple-800">Add Assembly Rule</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Assembly Type *</label>
                          <select
                            value={newRule.assemblyType || ''}
                            onChange={e => setNewRule({ ...newRule, assemblyType: e.target.value as AssemblyType })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select...</option>
                            {ASSEMBLY_TYPES.map(type => (
                              <option key={type} value={type}>{type.replace('_', ' ').toUpperCase()}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Quantity Formula *</label>
                          <input
                            type="text"
                            placeholder="e.g., 4, springs * 2, holes"
                            value={newRule.quantityFormula || ''}
                            onChange={e => setNewRule({ ...newRule, quantityFormula: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Cut Formula</label>
                          <input
                            type="text"
                            placeholder="e.g., H_system, W_system - 14.5"
                            value={newRule.cutFormula || ''}
                            onChange={e => setNewRule({ ...newRule, cutFormula: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Workstation</label>
                          <select
                            value={newRule.workstation || ''}
                            onChange={e => setNewRule({ ...newRule, workstation: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">None</option>
                            <option value="SAW">SAW</option>
                            <option value="ASSEMBLY">ASSEMBLY</option>
                            <option value="TRACKDRILL">TRACKDRILL</option>
                            <option value="CUTTABLE">CUTTABLE</option>
                            <option value="SHIPBOX">SHIPBOX</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input
                          type="text"
                          placeholder="Optional notes..."
                          value={newRule.notes || ''}
                          onChange={e => setNewRule({ ...newRule, notes: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      {/* Positioning Section */}
                      <div className="border-t border-purple-200 pt-3 mt-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!(newRule.motorSidePosition !== undefined || newRule.idlerSidePosition !== undefined)}
                            onChange={e => {
                              if (e.target.checked) {
                                setNewRule({ ...newRule, motorSidePosition: 0, idlerSidePosition: 0, positionDeductionApplies: false, positionDeductionMethod: 'split_evenly' });
                              } else {
                                const { motorSidePosition, idlerSidePosition, positionDeductionApplies, positionDeductionMethod, ...rest } = newRule;
                                setNewRule(rest as any);
                              }
                            }}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-purple-800">This part has positioning requirements</span>
                          <InfoTooltip text="Check this if the part installs at a specific measured position on another component (e.g., plug at 6 inches from tube end)" />
                        </label>

                        {(newRule.motorSidePosition !== undefined || newRule.idlerSidePosition !== undefined) && (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Motor Side (in) <InfoTooltip text="Distance in inches from the motor-side tube end to where this part installs" />
                                </label>
                                <input
                                  type="number"
                                  step="0.0625"
                                  placeholder="e.g., 6"
                                  value={newRule.motorSidePosition ?? ''}
                                  onChange={e => setNewRule({ ...newRule, motorSidePosition: e.target.value ? parseFloat(e.target.value) : undefined })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Idler Side (in) <InfoTooltip text="Distance in inches from the idler-side tube end to where this part installs" />
                                </label>
                                <input
                                  type="number"
                                  step="0.0625"
                                  placeholder="e.g., 5.75"
                                  value={newRule.idlerSidePosition ?? ''}
                                  onChange={e => setNewRule({ ...newRule, idlerSidePosition: e.target.value ? parseFloat(e.target.value) : undefined })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newRule.positionDeductionApplies || false}
                                onChange={e => setNewRule({ ...newRule, positionDeductionApplies: e.target.checked })}
                                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-700">Adjust for Factory Deductions</span>
                              <InfoTooltip text="When the order has 'Apply Factory Deductions' enabled, the deduction amount is ADDED to the position (part moves outward, greater distance from tube end)" />
                            </label>

                            {newRule.positionDeductionApplies && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Deduction Method <InfoTooltip text="How to distribute the factory deduction: Split Evenly divides between both sides, Motor Only adds full amount to motor side, Idler Only adds full amount to idler side" />
                                </label>
                                <select
                                  value={newRule.positionDeductionMethod || 'split_evenly'}
                                  onChange={e => setNewRule({ ...newRule, positionDeductionMethod: e.target.value as any })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                                >
                                  {POSITION_DEDUCTION_METHODS.map(method => (
                                    <option key={method.value} value={method.value}>{method.label} - {method.description}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setNewRule(null)}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleAddRule}
                          disabled={!newRule.assemblyType || !newRule.quantityFormula}
                          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                        >
                          Add Rule
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setNewRule({ assemblyType: '' as AssemblyType, quantityFormula: '' })}
                      className="w-full py-2 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50 text-sm font-medium"
                    >
                      + Add Assembly Rule
                    </button>
                  )}
                </div>
              )}
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (item ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// View Details Modal - Read-only view of all item info
function ViewDetailsModal({
  item,
  onClose,
  onEdit
}: {
  item: InventoryItem;
  onClose: () => void;
  onEdit: () => void;
}) {
  const i = item as any;

  const InfoRow = ({ label, value }: { label: string; value: string | number | boolean | undefined | null }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
      <div className="flex justify-between py-2 border-b border-gray-100">
        <span className="text-gray-500 text-sm">{label}</span>
        <span className="text-gray-900 text-sm font-medium text-right max-w-[60%]">
          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
        </span>
      </div>
    );
  };

  const partNumber = i?.partNumber || i?.part_number || '';
  const description = i?.description || '';
  const supplierDescription = i?.supplierDescription || i?.supplier_description || '';
  const sku = i?.sku || '';
  const supplierPartNumber = i?.supplierPartNumber || i?.supplier_part_number || '';
  const inventoryGroup = i?.inventoryGroup || i?.inventory_group || '';
  const supplierName = i?.supplierName || i?.supplier_name || '';
  const unitCost = i?.unitCost ?? i?.unit_price ?? 0;
  const quantityOnHand = i?.quantityOnHand ?? i?.on_hand ?? 0;
  const reorderQuantity = i?.reorderQuantity ?? i?.reorder_quantity ?? 0;
  const minQuantity = i?.minQuantity ?? i?.min_quantity ?? 0;
  const maxQuantity = i?.maxQuantity ?? i?.max_quantity ?? 0;
  const supplierMinOrder = i?.supplierMinOrder ?? i?.supplier_min_order ?? 0;
  const leadTimeDays = i?.leadTimeDays ?? i?.lead_time_days;
  const binLocation = i?.binLocation || i?.bin_location || '';
  const componentType = i?.componentType || i?.component_type || '';
  const stockTakeType = i?.stockTakeType || i?.stock_take_type || '';
  const quantityFormula = i?.quantityFormula || i?.quantity_formula || '';
  const cutFormula = i?.cutFormula || i?.cut_formula || '';
  const isExtrusion = i?.isExtrusion || i?.is_extrusion || false;
  const stockLength = i?.stockLength ?? i?.stock_length;
  const minUsableDrop = i?.minUsableDrop ?? i?.min_usable_drop;
  const rollWidth = i?.rollWidth ?? i?.roll_width;
  const notes = i?.notes || '';
  const imageFilename = i?.imageFilename || i?.image_filename || '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{partNumber}</h2>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Image */}
          {imageFilename && (
            <div className="flex justify-center">
              <img
                src={imageFilename.startsWith('http') ? imageFilename : `/uploads/parts/${imageFilename}`}
                alt={partNumber}
                className="max-h-48 rounded-lg shadow-md"
              />
            </div>
          )}

          {/* Basic Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Basic Info</h3>
            <InfoRow label="Part Number" value={partNumber} />
            <InfoRow label="SKU" value={sku} />
            <InfoRow label="Supplier Part #" value={supplierPartNumber} />
            <InfoRow label="Description" value={description} />
            <InfoRow label="Supplier Description" value={supplierDescription} />
            <InfoRow label="Group" value={inventoryGroup} />
            <InfoRow label="Component Type" value={componentType} />
            <InfoRow label="Supplier" value={supplierName} />
          </div>

          {/* Stock Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-3">Stock Info</h3>
            <InfoRow label="On Hand" value={quantityOnHand} />
            <InfoRow label="Unit Cost" value={unitCost ? `$${Number(unitCost).toFixed(2)}` : undefined} />
            <InfoRow label="Reorder Qty" value={reorderQuantity} />
            <InfoRow label="Min Qty" value={minQuantity} />
            <InfoRow label="Max Qty" value={maxQuantity} />
            <InfoRow label="Supplier Min Order" value={supplierMinOrder || undefined} />
            <InfoRow label="Lead Time" value={leadTimeDays ? `${leadTimeDays} days` : undefined} />
            <InfoRow label="Bin Location" value={binLocation} />
          </div>

          {/* BOM Rules (if any) */}
          {(stockTakeType || quantityFormula || cutFormula) && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-yellow-700 uppercase tracking-wide mb-3">BOM Rules</h3>
              <InfoRow label="Stock Take Type" value={stockTakeType} />
              <InfoRow label="Quantity Formula" value={quantityFormula} />
              <InfoRow label="Cut Formula" value={cutFormula} />
              <InfoRow label="Is Extrusion" value={isExtrusion} />
              <InfoRow label="Stock Length" value={stockLength ? `${stockLength}"` : undefined} />
              <InfoRow label="Min Usable Drop" value={minUsableDrop ? `${minUsableDrop}"` : undefined} />
              <InfoRow label="Roll Width" value={rollWidth ? `${rollWidth}"` : undefined} />
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Notes</h3>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">{notes}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ✏️ Edit Item
          </button>
        </div>
      </div>
    </div>
  );
}

// Adjust Stock Modal
function AdjustStockModal({
  item,
  onAdjust,
  onClose
}: {
  item: InventoryItem;
  onAdjust: (newQty: number, notes: string) => Promise<void>;
  onClose: () => void;
}) {
  const [adjustType, setAdjustType] = useState<AdjustmentType>('add');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Get display unit info for this item
  const displayUnit = (item as any).displayUnit || (item as any).display_unit;
  const conversionFactor = (item as any).conversionFactor || (item as any).conversion_factor || 1;
  const hasDisplayUnit = displayUnit && displayUnit !== 'each' && conversionFactor !== 1;

  // Calculate new quantity in storage units
  // User enters in display units, we convert to storage units
  const calculateNewQty = () => {
    const enteredQty = parseFloat(quantity) || 0;
    // Convert entered display unit value to storage units
    const qtyInStorageUnits = hasDisplayUnit ? enteredQty * conversionFactor : enteredQty;
    const currentOnHand = getQuantityOnHand(item);
    switch (adjustType) {
      case 'add': return currentOnHand + qtyInStorageUnits;
      case 'remove': return Math.max(0, currentOnHand - qtyInStorageUnits);
      case 'set': return qtyInStorageUnits;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onAdjust(calculateNewQty(), notes);
      onClose();
    } catch (err) {
      console.error('Adjustment failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Adjust Stock</h2>
          <p className="text-sm text-gray-500 mt-1">{item.part_number} - {item.description}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Current On Hand</p>
            <p className="text-3xl font-bold text-gray-900">{formatQuantityWithUnit(getQuantityOnHand(item), item)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Adjustment Type</label>
            <div className="grid grid-cols-3 gap-2">
              {(['add', 'remove', 'set'] as AdjustmentType[]).map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAdjustType(type)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${
                    adjustType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type === 'add' ? '+ Add' : type === 'remove' ? '- Remove' : '= Set To'}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity{hasDisplayUnit ? ` (${displayUnit})` : ''}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              required
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
              placeholder={hasDisplayUnit ? `Enter ${displayUnit}` : 'Enter quantity (decimals allowed)'}
            />
          </div>

          {quantity && (
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">New On Hand</p>
              <p className="text-3xl font-bold text-blue-700">{formatQuantityWithUnit(calculateNewQty(), item)}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Reason for adjustment..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !quantity}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Apply Adjustment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Receive Stock Modal - for receiving inventory from supplier
// Supports display unit conversion (e.g., Keder spline in yards)
function ReceiveStockModal({
  item,
  onReceive,
  onClose
}: {
  item: InventoryItem;
  onReceive: (params: {
    quantity: number;
    quantityInDisplayUnits: boolean;
    rollCount?: number;
    poNumber?: string;
    notes?: string;
  }) => Promise<void>;
  onClose: () => void;
}) {
  const [quantity, setQuantity] = useState('');
  const [rollCount, setRollCount] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  // Get display unit info
  const displayUnit = (item as any).displayUnit || (item as any).display_unit;
  const conversionFactor = (item as any).conversionFactor || (item as any).conversion_factor || 1;
  const hasDisplayUnit = displayUnit && displayUnit !== 'each' && conversionFactor !== 1;

  // Typical order threshold for warning (600 yards for Keder spline)
  const TYPICAL_ORDER_MIN = hasDisplayUnit ? 500 : 100;
  const TYPICAL_ORDER_MAX = hasDisplayUnit ? 1000 : 10000;

  const enteredQty = parseFloat(quantity) || 0;
  const isUnusualAmount = enteredQty > 0 && (enteredQty < TYPICAL_ORDER_MIN * 0.1 || enteredQty > TYPICAL_ORDER_MAX * 2);

  // Calculate new quantity (in storage units for display)
  const calculateNewQty = () => {
    const currentOnHand = getQuantityOnHand(item);
    const addedInStorageUnits = hasDisplayUnit ? enteredQty * conversionFactor : enteredQty;
    return currentOnHand + addedInStorageUnits;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Show warning for unusual amounts
    if (isUnusualAmount && !showWarning) {
      setShowWarning(true);
      return;
    }

    setSaving(true);
    try {
      await onReceive({
        quantity: enteredQty,
        quantityInDisplayUnits: hasDisplayUnit,
        rollCount: rollCount ? parseInt(rollCount, 10) : undefined,
        poNumber: poNumber || undefined,
        notes: notes || undefined,
      });
      onClose();
    } catch (err) {
      console.error('Receive failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Receive Stock</h2>
          <p className="text-sm text-gray-500 mt-1">{item.part_number} - {item.description}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Current Stock */}
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500">Current Stock</p>
            <p className="text-3xl font-bold text-gray-900">{formatQuantityWithUnit(getQuantityOnHand(item), item)}</p>
          </div>

          {/* Quantity Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {hasDisplayUnit ? `${displayUnit.charAt(0).toUpperCase() + displayUnit.slice(1)} Received` : 'Quantity Received'}
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              required
              value={quantity}
              onChange={e => {
                setQuantity(e.target.value);
                setShowWarning(false);
              }}
              onWheel={(e) => e.currentTarget.blur()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
              placeholder={hasDisplayUnit ? `Enter ${displayUnit}` : 'Enter quantity'}
            />
          </div>

          {/* Warning for unusual amounts */}
          {showWarning && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Unusual amount:</strong> You entered {enteredQty} {hasDisplayUnit ? displayUnit : 'units'}.
                Typical orders are {TYPICAL_ORDER_MIN}-{TYPICAL_ORDER_MAX} {hasDisplayUnit ? displayUnit : 'units'}.
              </p>
              <p className="text-sm text-yellow-700 mt-1">Click "Receive Stock" again to confirm.</p>
            </div>
          )}

          {/* Roll Count (optional, for linear materials) */}
          {hasDisplayUnit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Rolls <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="number"
                min="1"
                step="1"
                value={rollCount}
                onChange={e => setRollCount(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="e.g., 12"
              />
              <p className="text-xs text-gray-500 mt-1">For reference only - individual roll lengths not tracked</p>
            </div>
          )}

          {/* PO / Reference Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PO / Reference # <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={poNumber}
              onChange={e => setPoNumber(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="e.g., PO-12345"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Additional notes..."
            />
          </div>

          {/* New Stock Preview */}
          {quantity && (
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">New Stock</p>
              <p className="text-3xl font-bold text-green-700">{formatQuantityWithUnit(calculateNewQty(), item)}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !quantity || enteredQty <= 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Receiving...' : showWarning ? 'Confirm Receive' : 'Receive Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteModal({
  item,
  onConfirm,
  onClose
}: {
  item: InventoryItem;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🗑️</span>
          </div>
          <h2 className="text-xl font-semibold text-center mb-2">Delete Item?</h2>
          <p className="text-gray-500 text-center mb-4">
            Are you sure you want to delete <strong>{item.part_number}</strong>?
            <br />
            <span className="text-sm">{item.description}</span>
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Image Upload Modal
function ImageUploadModal({
  item,
  onUploadComplete,
  onClose
}: {
  item: InventoryItem;
  onUploadComplete: (filename: string | null) => void;
  onClose: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB)');
      return;
    }

    setUploading(true);
    setProgress(30);
    setError(null);

    try {
      const result = await api.inventory.uploadImage(item.id!, file);
      setProgress(100);
      setTimeout(() => {
        onUploadComplete(result.image_filename);
        onClose();
      }, 300);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Remove this image?')) return;
    
    setUploading(true);
    try {
      await api.inventory.deleteImage(item.id!);
      onUploadComplete(null);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  // Handle both full URLs (Cloudinary) and relative paths (legacy)
  // Also handle both camelCase (from API) and snake_case (from local updates)
  const imgFile = (item as any).imageFilename || item.image_filename;
  const imageUrl = imgFile
    ? (imgFile.startsWith('http') ? imgFile : `/uploads/parts/${imgFile}`)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Part Image</h2>
          <p className="text-sm text-gray-500 mt-1">{item.part_number} - {item.description}</p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Current Image Preview */}
          <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
            {imageUrl ? (
              <img src={imageUrl} alt={item.part_number} className="max-w-full max-h-48 object-contain" />
            ) : (
              <div className="text-gray-400 text-center">
                <span className="text-4xl">📷</span>
                <p className="text-sm mt-2">No image uploaded</p>
              </div>
            )}
          </div>

          {/* Upload Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('imageFileInput')?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <input
              type="file"
              id="imageFileInput"
              accept="image/*"
              onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="hidden"
            />
            <span className="text-2xl">📤</span>
            <p className="text-sm text-gray-600 mt-2">
              {dragActive ? 'Drop image here' : 'Click or drag image to upload'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Max 10MB, JPG/PNG/GIF</p>
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-300" 
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Close
            </button>
            {imageUrl && (
              <button
                onClick={handleDelete}
                disabled={uploading}
                className="px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
              >
                🗑️ Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// CSV Import Modal - Multi-step wizard
interface CSVRow { [key: string]: string }
interface FieldMapping { [csvColumn: string]: string }

const DB_FIELDS = [
  { name: 'partNumber', label: 'Part Number', required: true },
  { name: 'description', label: 'Description', required: false },
  { name: 'supplierDescription', label: 'Supplier Description' },
  { name: 'sku', label: 'SKU' },
  { name: 'supplierPartNumber', label: 'Supplier Part Number' },
  { name: 'supplierName', label: 'Supplier / Vendor Name' },
  { name: 'inventoryGroup', label: 'Inventory Group' },
  { name: 'quantityOnHand', label: 'Quantity On Hand' },
  { name: 'unitCost', label: 'Unit Cost' },
  { name: 'reorderQuantity', label: 'Reorder Quantity' },
  { name: 'leadTimeDays', label: 'Lead Time (days)' },
  { name: 'minQuantity', label: 'Min Quantity' },
  { name: 'maxQuantity', label: 'Max Quantity' },
  { name: 'binLocation', label: 'Bin Location' },
  { name: 'notes', label: 'Notes' },
  { name: 'stockTakeType', label: 'Stock Take Type' },
  { name: 'quantityFormula', label: 'Quantity Formula' },
  { name: 'cutFormula', label: 'Cut Formula' },
  { name: 'componentType', label: 'Component Type' },
  { name: 'isExtrusion', label: 'Is Extrusion' },
  { name: 'stockLength', label: 'Stock Length' },
  { name: 'minUsableDrop', label: 'Min Usable Drop' },
  { name: 'colorVariant', label: 'Color Variant' },
  { name: 'appliesTo', label: 'Applies To' },
  { name: 'isActive', label: 'Active' },
];

const AUTO_MAPPINGS: { [key: string]: string } = {
  // Part Number variations
  'part number': 'partNumber', 'part_number': 'partNumber', 'partno': 'partNumber',
  'part no': 'partNumber', 'item': 'partNumber', 'partnumber': 'partNumber',

  // Supplier Part Number
  'supplier part': 'supplierPartNumber', 'supplier_part_number': 'supplierPartNumber',
  'supplierpartnumber': 'supplierPartNumber', 'vendor part': 'supplierPartNumber',

  // Description
  'description': 'description', 'desc': 'description', 'name': 'description',

  // Supplier Description (for reordering)
  'vendordescription': 'supplierDescription', 'vendor description': 'supplierDescription',
  'supplier_description': 'supplierDescription', 'supplier description': 'supplierDescription',
  'supplierdescription': 'supplierDescription',

  // SKU
  'sku': 'sku', 'catalog_sku': 'sku', 'catalog sku': 'sku', 'catalogsku': 'sku',

  // Inventory Group / Category
  'group': 'inventoryGroup', 'inventory_group': 'inventoryGroup', 'inventory group': 'inventoryGroup',
  'inventorygroup': 'inventoryGroup', 'category': 'inventoryGroup', 'cat': 'inventoryGroup',
  'catorgy': 'inventoryGroup', // common typo

  // Quantity on Hand
  'qty': 'quantityOnHand', 'on_hand': 'quantityOnHand', 'on hand': 'quantityOnHand',
  'quantity': 'quantityOnHand', 'stock': 'quantityOnHand', 'onhand': 'quantityOnHand',
  'in stock': 'quantityOnHand', 'instock': 'quantityOnHand', 'qty on hand': 'quantityOnHand',
  'qtyonhand': 'quantityOnHand', 'quantityonhand': 'quantityOnHand', 'quantity_on_hand': 'quantityOnHand',

  // Unit Cost
  'price': 'unitCost', 'unit_price': 'unitCost', 'unit price': 'unitCost',
  'cost': 'unitCost', 'unitprice': 'unitCost', 'unitcost': 'unitCost', 'unit_cost': 'unitCost',

  // Reorder Quantity
  'reorder': 'reorderQuantity', 'reorder_quantity': 'reorderQuantity',
  'reorderquantity': 'reorderQuantity', 'reorder qty': 'reorderQuantity',

  // Min/Max Quantity
  'min qty': 'minQuantity', 'min_qty': 'minQuantity', 'min_quantity': 'minQuantity',
  'minquantity': 'minQuantity', 'min quantity': 'minQuantity',
  'max qty': 'maxQuantity', 'max_qty': 'maxQuantity', 'max_quantity': 'maxQuantity',
  'maxquantity': 'maxQuantity', 'max quantity': 'maxQuantity',

  // Lead Time
  'lead_time': 'leadTimeDays', 'lead time': 'leadTimeDays', 'leadtime': 'leadTimeDays',
  'lead_time_days': 'leadTimeDays', 'lead time days': 'leadTimeDays', 'leadtimedays': 'leadTimeDays',

  // Bin Location
  'bin': 'binLocation', 'bin_location': 'binLocation', 'binlocation': 'binLocation',
  'location': 'binLocation', 'bin location': 'binLocation',

  // Supplier / Vendor Name
  'supplier': 'supplierName', 'vendor': 'supplierName', 'supplier name': 'supplierName',
  'supplier_name': 'supplierName', 'suppliername': 'supplierName', 'vendor name': 'supplierName',
  'vendor_name': 'supplierName', 'vendorname': 'supplierName', 'mfr': 'supplierName',
  'manufacturer': 'supplierName',

  // Notes
  'notes': 'notes', 'note': 'notes', 'comments': 'notes',

  // Stock Take Type
  'stock_take_type': 'stockTakeType', 'stocktaketype': 'stockTakeType',
  'stock take type': 'stockTakeType',

  // Quantity Formula
  'quantity_formula': 'quantityFormula', 'quantityformula': 'quantityFormula',
  'qty formula': 'quantityFormula',

  // Cut Formula
  'cut_formula': 'cutFormula', 'cutformula': 'cutFormula', 'cut formula': 'cutFormula',

  // Component Type
  'component_type': 'componentType', 'componenttype': 'componentType',
  'component type': 'componentType', 'type': 'componentType',

  // Is Extrusion
  'is_extrusion': 'isExtrusion', 'isextrusion': 'isExtrusion', 'extrusion': 'isExtrusion',

  // Stock Length
  'stock_length': 'stockLength', 'stocklength': 'stockLength', 'stock length': 'stockLength',

  // Min Usable Drop
  'min_usable_drop': 'minUsableDrop', 'minusabledrop': 'minUsableDrop',

  // Color Variant
  'color_variant': 'colorVariant', 'colorvariant': 'colorVariant', 'color variant': 'colorVariant',
  'color': 'colorVariant', 'system color': 'colorVariant', 'systemcolor': 'colorVariant',

  // Applies To
  'applies_to': 'appliesTo', 'appliesto': 'appliesTo', 'applies to': 'appliesTo',
  'conditions': 'appliesTo',

  // Active
  'active': 'isActive', 'is_active': 'isActive', 'isactive': 'isActive', 'status': 'isActive',
};

// Convert snake_case to camelCase for API
const SNAKE_TO_CAMEL: { [key: string]: string } = {
  'part_number': 'partNumber',
  'partNumber': 'partNumber',
  'supplier_part_number': 'supplierPartNumber',
  'supplierPartNumber': 'supplierPartNumber',
  'description': 'description',
  'supplier_description': 'supplierDescription',
  'supplierDescription': 'supplierDescription',
  'sku': 'sku',
  'inventory_group': 'inventoryGroup',
  'inventoryGroup': 'inventoryGroup',
  'supplier_name': 'supplierName',
  'supplierName': 'supplierName',
  'unit_price': 'unitCost',
  'unitCost': 'unitCost',
  'unit_cost': 'unitCost',
  'dealer_price': 'dealerPrice',
  'dealerPrice': 'dealerPrice',
  'on_hand': 'quantityOnHand',
  'quantityOnHand': 'quantityOnHand',
  'quantity_on_hand': 'quantityOnHand',
  'reorder_quantity': 'reorderQuantity',
  'reorderQuantity': 'reorderQuantity',
  'min_quantity': 'minQuantity',
  'minQuantity': 'minQuantity',
  'max_quantity': 'maxQuantity',
  'maxQuantity': 'maxQuantity',
  'bin_location': 'binLocation',
  'binLocation': 'binLocation',
  'notes': 'notes',
  'active': 'isActive',
  'isActive': 'isActive',
  'is_active': 'isActive',
  'stock_take_type': 'stockTakeType',
  'stockTakeType': 'stockTakeType',
  'quantity_formula': 'quantityFormula',
  'quantityFormula': 'quantityFormula',
  'cut_formula': 'cutFormula',
  'cutFormula': 'cutFormula',
  'component_type': 'componentType',
  'componentType': 'componentType',
  'applies_to': 'appliesTo',
  'appliesTo': 'appliesTo',
  'is_extrusion': 'isExtrusion',
  'isExtrusion': 'isExtrusion',
  'stock_length': 'stockLength',
  'stockLength': 'stockLength',
  'min_usable_drop': 'minUsableDrop',
  'minUsableDrop': 'minUsableDrop',
  'roll_width': 'rollWidth',
  'rollWidth': 'rollWidth',
  'color_variant': 'colorVariant',
  'colorVariant': 'colorVariant',
  'supplier_id': 'supplierId',
  'supplierId': 'supplierId',
  'lead_time': 'leadTimeDays',
  'lead_time_days': 'leadTimeDays',
  'leadTimeDays': 'leadTimeDays',
  'inventory_groups': 'inventoryGroups',
  'inventoryGroups': 'inventoryGroups',
  'product_lines': 'productLines',
  'productLines': 'productLines',
};

// Valid API fields (only these will be sent to the API)
const VALID_API_FIELDS = new Set([
  'partNumber',
  'supplierPartNumber',
  'description',
  'supplierDescription',
  'sku',
  'quantityOnHand',
  'supplierId',
  'supplierName',
  'inventoryGroup',
  'inventoryGroups',
  'productLines',
  'minQuantity',
  'maxQuantity',
  'reorderQuantity',
  'unitCost',
  'dealerPrice',
  'leadTimeDays',
  'binLocation',
  'notes',
  'stockTakeType',
  'quantityFormula',
  'cutFormula',
  'componentType',
  'appliesTo',
  'isExtrusion',
  'stockLength',
  'minUsableDrop',
  'rollWidth',
  'colorVariant',
  'isActive',
]);

// Valid stockTakeType values (must match backend enum)
const VALID_STOCK_TAKE_TYPES = new Set([
  'fixed', 'per_screen', 'per_order', 'calculated', 'cut_length', 'linear_from_roll', 'conditional', 'color_variant'
]);

// Helper function to convert snake_case object to camelCase API format
function toApiFormat(data: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    // Skip null/undefined values
    if (value === null || value === undefined || value === '') continue;

    // Convert key to camelCase
    const camelKey = SNAKE_TO_CAMEL[key] || key;

    // Only include if it's a valid API field
    if (VALID_API_FIELDS.has(camelKey)) {
      result[camelKey] = value;
    }
  }

  // Validate reorderQuantity: must be >= 1 if provided, otherwise remove it
  if (result.reorderQuantity !== undefined) {
    const qty = Number(result.reorderQuantity);
    if (isNaN(qty) || qty < 1) {
      delete result.reorderQuantity; // Let backend use default
    }
  }

  // Validate stockTakeType: must be a valid enum value, otherwise remove it
  if (result.stockTakeType !== undefined) {
    const stockType = String(result.stockTakeType).toLowerCase();
    if (!VALID_STOCK_TAKE_TYPES.has(stockType)) {
      delete result.stockTakeType; // Invalid value, remove it
    } else {
      result.stockTakeType = stockType; // Ensure lowercase
    }
  }

  return result;
}

function ImportModal({
  groups,
  onImportComplete,
  onClose
}: {
  groups: InventoryGroup[];
  onImportComplete: () => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [targetGroup, setTargetGroup] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{ created: number; updated: number; skipped: number; errors: string[] } | null>(null);

  // Parse CSV file
  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return;

    // Parse header row
    const headers = parseCSVLine(lines[0]);
    setCsvHeaders(headers);

    // Parse data rows
    const data: CSVRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.some(v => v.trim())) {
        const row: CSVRow = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });
        data.push(row);
      }
    }
    setCsvData(data);

    // Auto-map fields
    const mappings: FieldMapping = {};
    headers.forEach(header => {
      const normalized = header.toLowerCase().trim();
      if (AUTO_MAPPINGS[normalized]) {
        mappings[header] = AUTO_MAPPINGS[normalized];
      }
    });
    setFieldMappings(mappings);
    setStep(2);
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target?.result as string;
        parseCSV(text);
      };
      reader.readAsText(file);
    }
  };

  const updateMapping = (csvCol: string, dbField: string) => {
    setFieldMappings(prev => {
      const updated = { ...prev };
      // Remove any other mapping to this db field
      Object.keys(updated).forEach(key => {
        if (updated[key] === dbField && key !== csvCol) {
          delete updated[key];
        }
      });
      if (dbField) {
        updated[csvCol] = dbField;
      } else {
        delete updated[csvCol];
      }
      return updated;
    });
  };

  const doImport = async () => {
    setImporting(true);
    setImportProgress(0);
    let created = 0, updated = 0, skipped = 0;
    const errors: string[] = [];

    // Numeric fields that need conversion
    const NUMERIC_FIELDS = [
      'unit_price', 'unitCost', 'unit_cost',
      'on_hand', 'quantityOnHand', 'quantity_on_hand',
      'reorder_quantity', 'reorderQuantity',
      'min_quantity', 'minQuantity',
      'max_quantity', 'maxQuantity',
      'stock_length', 'stockLength',
      'min_usable_drop', 'minUsableDrop',
      'lead_time', 'lead_time_days', 'leadTimeDays',
    ];

    // Boolean fields that need conversion
    const BOOLEAN_FIELDS = ['active', 'isActive', 'is_active', 'is_extrusion', 'isExtrusion'];

    for (let i = 0; i < csvData.length; i++) {
      setImportProgress(Math.round((i / csvData.length) * 100));
      const row = csvData[i];

      // Build item from mappings
      const rawItem: Record<string, string | number | boolean> = {};
      Object.keys(fieldMappings).forEach(csvCol => {
        const dbField = fieldMappings[csvCol];
        let value: string | number | boolean = row[csvCol]?.trim() || '';

        // Convert numeric fields
        if (NUMERIC_FIELDS.includes(dbField)) {
          value = parseFloat(String(value).replace(/[^0-9.-]/g, '')) || 0;
        }
        // Convert boolean fields
        if (BOOLEAN_FIELDS.includes(dbField)) {
          value = ['true', '1', 'yes', 'y'].includes(String(value).toLowerCase());
        }

        if (value !== '' && value !== null) {
          rawItem[dbField] = value;
        }
      });

      // Apply target group if set (fieldMappings now maps to camelCase)
      if (targetGroup && !rawItem.inventoryGroup) {
        rawItem.inventoryGroup = targetGroup;
      }

      // Get part number (now in camelCase from fieldMappings)
      // Normalize: convert to string, strip trailing .0 from whole numbers (e.g., "31101.0" → "31101")
      let partNumber = String(rawItem.partNumber || '').trim();
      if (partNumber.endsWith('.0')) {
        partNumber = partNumber.slice(0, -2);
      }
      rawItem.partNumber = partNumber;

      // Skip if no part number
      if (!partNumber) {
        skipped++;
        continue;
      }

      // Convert to API format (camelCase, only valid fields)
      const apiItem = toApiFormat(rawItem);

      try {
        // Check if item exists - normalize comparison (strip .0 from both sides)
        // Note: API may return partNumber (camelCase) or part_number (snake_case)
        const existingItems = await api.inventory.getAll({ search: partNumber });
        const normalizePartNum = (p: string) => p?.endsWith('.0') ? p.slice(0, -2) : (p || '');
        const existing = existingItems.find((i: InventoryItem & { partNumber?: string }) => {
          const itemPartNum = i.part_number || i.partNumber || '';
          return normalizePartNum(itemPartNum) === partNumber;
        });

        if (existing) {
          await api.inventory.update(existing.id!, apiItem);
          updated++;
        } else {
          await api.inventory.create(apiItem);
          created++;
        }
      } catch (err) {
        errors.push(`Row ${i + 1} (${partNumber}): ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }

    setImportProgress(100);
    setImportResults({ created, updated, skipped, errors });
    setStep(4);
    setImporting(false);
    onImportComplete();
  };

  const mappedFields = Object.values(fieldMappings);
  const hasRequiredFields = DB_FIELDS.filter(f => f.required).every(f => mappedFields.includes(f.name));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Import Inventory from CSV</h2>
            <p className="text-sm text-gray-500 mt-1">Step {step} of 4</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        {/* Steps indicator */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex gap-2">
            {['Upload', 'Map Fields', 'Review', 'Complete'].map((label, idx) => (
              <div key={label} className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg ${
                step === idx + 1 ? 'bg-blue-100 text-blue-700' :
                step > idx + 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
              }`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  step === idx + 1 ? 'bg-blue-600 text-white' :
                  step > idx + 1 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'
                }`}>{idx + 1}</span>
                <span className="text-sm font-medium hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div>
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => document.getElementById('csvFileInput')?.click()}
              >
                <div className="text-5xl mb-4">📄</div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">Drop CSV file here or click to browse</h3>
                <p className="text-sm text-gray-500">Supports .csv files</p>
                <input
                  id="csvFileInput"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* Step 2: Field Mapping */}
          {step === 2 && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Inventory Group (optional)</label>
                <select
                  value={targetGroup}
                  onChange={e => setTargetGroup(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Use from CSV or leave empty</option>
                  {groups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-gray-500 mb-3">Map your CSV columns to database fields. Required fields marked with *</p>
                {csvHeaders.map(header => {
                  const mapped = fieldMappings[header];
                  const isRequired = mapped && DB_FIELDS.find(f => f.name === mapped)?.required;
                  const sample = csvData[0]?.[header]?.substring(0, 50) || '';
                  return (
                    <div key={header} className={`flex items-center gap-4 p-3 rounded-lg border ${
                      isRequired ? 'border-l-4 border-l-blue-500 border-gray-200' :
                      mapped ? 'border-l-4 border-l-green-500 border-gray-200' : 'border-gray-200'
                    }`}>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{header}</div>
                        {sample && <div className="text-xs text-gray-400 truncate">e.g. {sample}</div>}
                      </div>
                      <span className="text-gray-400">→</span>
                      <select
                        value={mapped || ''}
                        onChange={e => updateMapping(header, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">-- Skip --</option>
                        {DB_FIELDS.map(f => (
                          <option key={f.name} value={f.name}>{f.label}{f.required ? ' *' : ''}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <div>
              <p className="text-sm text-gray-500 mb-4">Preview first 10 rows to be imported. Total: {csvData.length} rows</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left">#</th>
                      {Object.values(fieldMappings).map(dbField => {
                        const field = DB_FIELDS.find(f => f.name === dbField);
                        return <th key={dbField} className="border border-gray-200 px-3 py-2 text-left">{field?.label || dbField}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-3 py-2 text-gray-500">{idx + 1}</td>
                        {Object.entries(fieldMappings).map(([csvCol, _dbField]) => (
                          <td key={csvCol} className="border border-gray-200 px-3 py-2">{row[csvCol] || <span className="text-gray-300">empty</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && (
            <div className="text-center py-8">
              {importing ? (
                <div>
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700">Importing... {importProgress}%</p>
                  <div className="w-64 h-2 bg-gray-200 rounded-full mx-auto mt-4">
                    <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${importProgress}%` }} />
                  </div>
                </div>
              ) : importResults && (
                <div>
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Import Complete!</h3>
                  <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-green-600">{importResults.created}</p>
                      <p className="text-sm text-green-700">Created</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-blue-600">{importResults.updated}</p>
                      <p className="text-sm text-blue-700">Updated</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-gray-600">{importResults.skipped}</p>
                      <p className="text-sm text-gray-700">Skipped</p>
                    </div>
                  </div>
                  {importResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left max-w-lg mx-auto">
                      <p className="font-medium text-red-700 mb-2">{importResults.errors.length} Errors:</p>
                      <ul className="text-sm text-red-600 space-y-1 max-h-32 overflow-y-auto">
                        {importResults.errors.slice(0, 10).map((err, i) => <li key={i}>{err}</li>)}
                        {importResults.errors.length > 10 && <li>...and {importResults.errors.length - 10} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={() => step > 1 && !importing ? setStep(s => s - 1) : onClose()}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            {step === 1 || step === 4 ? 'Close' : 'Back'}
          </button>
          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              disabled={!hasRequiredFields}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Preview
            </button>
          )}
          {step === 3 && (
            <button
              onClick={doImport}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              🚀 Start Import ({csvData.length} rows)
            </button>
          )}
          {step === 4 && !importing && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Inventory Page
export default function InventoryPage() {
  // Data state
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [productLines, setProductLines] = useState<ProductLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [stockFilter, setStockFilter] = useState<StockFilter>('all');

  // Sort state
  const [sortField, setSortField] = useState<SortField>('part_number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Modal state
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [viewingItem, setViewingItem] = useState<InventoryItem | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null);
  const [receivingItem, setReceivingItem] = useState<InventoryItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<InventoryItem | null>(null);
  const [imageUploadItem, setImageUploadItem] = useState<InventoryItem | null>(null);

  // Toast state
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = (message: string, type: Toast['type'] = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };
  const dismissToast = (id: number) => setToasts(prev => prev.filter(t => t.id !== id));

  // Load data
  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [itemsData, groupsData, productLinesData] = await Promise.all([
        api.inventory.getAll({ limit: 10000 }),
        api.inventory.getInventoryGroups().catch(() => []),
        api.inventory.getProductLinesWithVersions().catch(() => [])
      ]);

      setItems(itemsData || []);
      setGroups(groupsData || []);
      setProductLines(productLinesData || []);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setError('Failed to load inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Filter and sort items
  const filteredItems = items.filter(item => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches = 
        item.part_number?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.supplier_part_number?.toLowerCase().includes(query) ||
        item.supplier_name?.toLowerCase().includes(query);
      if (!matches) return false;
    }

    // Group filter - support both legacy single group and new array
    if (groupFilter) {
      const groups = item.inventoryGroups || item.inventory_groups || [];
      const legacyGroup = item.inventoryGroup || item.inventory_group;
      const inGroup = groups.includes(groupFilter) || legacyGroup === groupFilter;
      if (!inGroup) return false;
    }

    // Status filter
    if (statusFilter === 'active' && item.active === false) return false;
    if (statusFilter === 'inactive' && item.active !== false) return false;

    // Stock filter
    if (stockFilter !== 'all') {
      const onHand = item.quantityOnHand ?? item.on_hand ?? 0;
      const reorderQty = item.reorderQuantity ?? item.reorder_quantity ?? 0;
      if (stockFilter === 'out' && onHand !== 0) return false;
      if (stockFilter === 'low' && (onHand === 0 || onHand > reorderQty)) return false;
      if (stockFilter === 'ok' && onHand <= reorderQty) return false;
    }

    return true;
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];
    
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginate
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
  const paginatedItems = sortedItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, groupFilter, statusFilter, stockFilter]);

  // Stats - must match StockBadge logic exactly
  // Note: API returns quantityOnHand (camelCase), type has on_hand (snake_case) - check both
  const getOnHand = (i: InventoryItem) => i.quantityOnHand ?? i.on_hand ?? 0;
  const stats = {
    total: items.length,
    // OK badge: on_hand > reorder_quantity
    inStock: items.filter(i => getOnHand(i) > (i.reorderQuantity ?? i.reorder_quantity ?? 0)).length,
    // Low badge: on_hand > 0 AND on_hand <= reorder_quantity
    lowStock: items.filter(i => {
      const onHand = getOnHand(i);
      const reorderQty = i.reorderQuantity ?? i.reorder_quantity ?? 0;
      return onHand > 0 && onHand <= reorderQty;
    }).length,
    // Out badge: on_hand === 0
    outOfStock: items.filter(i => getOnHand(i) === 0).length,
  };

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSaveItem = async (data: Partial<InventoryItem>) => {
    // Convert snake_case to camelCase and filter to valid API fields
    const apiData = toApiFormat(data as Record<string, unknown>);

    if (editingItem) {
      // Remove partNumber from update - can't change part number
      const { partNumber, ...updateData } = apiData as Record<string, unknown>;
      await api.inventory.update(editingItem.id!, updateData);
      addToast(`Updated ${data.part_number || partNumber}`);
    } else {
      await api.inventory.create(apiData);
      addToast(`Created ${data.part_number || (apiData as Record<string, unknown>).partNumber}`);
    }
    loadInventory();
  };

  const handleAdjustStock = async (item: InventoryItem, newQty: number, notes: string) => {
    await api.inventory.adjustStock(item.id!, newQty, notes);
    addToast(`Adjusted ${item.part_number} to ${newQty}`);
    loadInventory();
  };

  const handleReceiveStock = async (
    item: InventoryItem,
    params: {
      quantity: number;
      quantityInDisplayUnits: boolean;
      rollCount?: number;
      poNumber?: string;
      notes?: string;
    }
  ) => {
    await api.inventory.receiveInventoryById({
      itemId: item.id!,
      ...params,
    });
    const displayUnit = (item as any).displayUnit || (item as any).display_unit;
    const hasDisplayUnit = displayUnit && displayUnit !== 'each';
    addToast(
      `Received ${params.quantity} ${hasDisplayUnit ? displayUnit : 'units'} of ${item.part_number}`,
      'success'
    );
    loadInventory();
  };

  const handleDeleteItem = async (item: InventoryItem) => {
    await api.inventory.delete(item.id!);
    addToast(`Deleted ${item.part_number}`, 'info');
    loadInventory();
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle="Manage parts and materials"
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <span>📥</span> Import CSV
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <span>+</span> Add Item
            </button>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card 
          className={`text-center cursor-pointer hover:shadow-md transition-shadow ${stockFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStockFilter('all')}
        >
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total Items</p>
        </Card>
        <Card 
          className={`text-center cursor-pointer hover:shadow-md transition-shadow ${stockFilter === 'ok' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setStockFilter(stockFilter === 'ok' ? 'all' : 'ok')}
        >
          <p className="text-2xl font-bold text-green-600">{stats.inStock}</p>
          <p className="text-sm text-gray-500">In Stock</p>
        </Card>
        <Card 
          className={`text-center cursor-pointer hover:shadow-md transition-shadow ${stockFilter === 'low' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}
        >
          <p className="text-2xl font-bold text-yellow-600">{stats.lowStock}</p>
          <p className="text-sm text-gray-500">Low Stock</p>
        </Card>
        <Card 
          className={`text-center cursor-pointer hover:shadow-md transition-shadow ${stockFilter === 'out' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setStockFilter(stockFilter === 'out' ? 'all' : 'out')}
        >
          <p className="text-2xl font-bold text-red-600">{stats.outOfStock}</p>
          <p className="text-sm text-gray-500">Out of Stock</p>
        </Card>
      </div>

      {/* Group Tabs */}
      <div className="mb-4 overflow-x-auto">
        <div className="flex gap-1 border-b border-gray-200 min-w-max pb-px">
          <button
            onClick={() => setGroupFilter('')}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap',
              groupFilter === ''
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            All ({items.length})
          </button>
          {groups.map(g => {
            const count = items.filter(i => {
              const itemGroups = i.inventoryGroups || i.inventory_groups || [];
              const legacyGroup = i.inventoryGroup || i.inventory_group;
              return itemGroups.includes(g.name) || legacyGroup === g.name;
            }).length;
            return (
              <button
                key={g.id}
                onClick={() => setGroupFilter(g.name)}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap',
                  groupFilter === g.name
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {g.name} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search part #, description, supplier..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All Status</option>
          </select>
          <button
            onClick={loadInventory}
            className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
            title="Refresh"
          >
            🔄
          </button>
        </div>
        <div className="mt-3 text-sm text-gray-500">
          Showing {paginatedItems.length} of {filteredItems.length} items
          {filteredItems.length !== items.length && ` (filtered from ${items.length})`}
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6 bg-red-50 border-red-200">
          <p className="text-red-700">{error}</p>
          <button onClick={loadInventory} className="mt-2 text-red-600 underline">Try again</button>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('part_number')}
                >
                  Part # <SortIndicator field="part_number" currentField={sortField} direction={sortDirection} />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('description')}
                >
                  Description <SortIndicator field="description" currentField={sortField} direction={sortDirection} />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('inventory_group')}
                >
                  Group <SortIndicator field="inventory_group" currentField={sortField} direction={sortDirection} />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('supplier_name')}
                >
                  Supplier <SortIndicator field="supplier_name" currentField={sortField} direction={sortDirection} />
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('on_hand')}
                >
                  On Hand <SortIndicator field="on_hand" currentField={sortField} direction={sortDirection} />
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reorder
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedItems.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{(item as any).partNumber || item.part_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={item.description}>
                    {item.description}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(item as any).inventoryGroup || item.inventory_group}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(item as any).supplierName || item.supplier_name}</td>
                  <td className="px-4 py-3 text-sm text-center font-medium">{formatQuantityWithUnit(getQuantityOnHand(item), item)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">
                    {((item as any).unitCost || item.unit_price) ? `$${Number((item as any).unitCost || item.unit_price).toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">{(item as any).reorderQuantity || item.reorder_quantity || '-'}</td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">
                    {((item as any).leadTimeDays || item.lead_time_days) ? `${(item as any).leadTimeDays || item.lead_time_days}d` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{(item as any).componentType || item.component_type || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <StockBadge onHand={(item as any).quantityOnHand ?? item.on_hand ?? 0} reorderQty={(item as any).reorderQuantity || item.reorder_quantity || 0} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(() => {
                      const imgFile = (item as any).imageFilename || item.image_filename;
                      return (
                        <button
                          onClick={() => setImageUploadItem(item)}
                          className="inline-block w-10 h-10 rounded border border-gray-200 overflow-hidden hover:border-blue-400 transition-colors"
                          title={imgFile ? 'Click to change image' : 'Click to add image'}
                        >
                          {imgFile ? (
                            <img
                              src={imgFile.startsWith('http') ? imgFile : `/uploads/parts/${imgFile}`}
                              alt={item.part_number}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="flex items-center justify-center w-full h-full text-gray-300 text-lg">📷</span>
                          )}
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => setAdjustingItem(item)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Adjust Stock"
                      >
                        ±
                      </button>
                      <button
                        onClick={() => setReceivingItem(item)}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Receive Stock"
                      >
                        📦
                      </button>
                      <button
                        onClick={() => setEditingItem(item)}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => setDeletingItem(item)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedItems.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-gray-500">
                    {filteredItems.length === 0 && items.length > 0 
                      ? 'No items match your filters' 
                      : 'No inventory items found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Modals */}
      {(showAddModal || editingItem) && (
        <ItemModal
          item={editingItem}
          groups={groups}
          productLines={productLines}
          onSave={handleSaveItem}
          onClose={() => { setShowAddModal(false); setEditingItem(null); }}
        />
      )}

      {viewingItem && (
        <ViewDetailsModal
          item={viewingItem}
          onClose={() => setViewingItem(null)}
          onEdit={() => { setEditingItem(viewingItem); setViewingItem(null); }}
        />
      )}

      {adjustingItem && (
        <AdjustStockModal
          item={adjustingItem}
          onAdjust={(qty, notes) => handleAdjustStock(adjustingItem, qty, notes)}
          onClose={() => setAdjustingItem(null)}
        />
      )}

      {receivingItem && (
        <ReceiveStockModal
          item={receivingItem}
          onReceive={(params) => handleReceiveStock(receivingItem, params)}
          onClose={() => setReceivingItem(null)}
        />
      )}

      {deletingItem && (
        <DeleteModal
          item={deletingItem}
          onConfirm={() => handleDeleteItem(deletingItem)}
          onClose={() => setDeletingItem(null)}
        />
      )}

      {imageUploadItem && (
        <ImageUploadModal
          item={imageUploadItem}
          onUploadComplete={(filename) => {
            // Update local items array
            setItems(prev => prev.map(i =>
              i.id === imageUploadItem.id
                ? { ...i, image_filename: filename }
                : i
            ));
            addToast(filename ? 'Image uploaded successfully!' : 'Image removed!', 'success');
            setImageUploadItem(null);
          }}
          onClose={() => setImageUploadItem(null)}
        />
      )}

      {showImportModal && (
        <ImportModal
          groups={groups}
          onImportComplete={() => {
            loadInventory();
            addToast('Import completed!', 'success');
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}